import React, { useEffect, useState } from "react";
import api, { formatMoney } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from "recharts";
import { PieChart as PieIcon, TrendingUp, DollarSign, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const COLORS = ["#F59E0B", "#10B981", "#3B82F6", "#EF4444", "#A855F7", "#EC4899", "#14B8A6"];

const chartTooltip = {
  contentStyle: {
    background: "#0B111C",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 8,
    fontSize: 12,
  },
  labelStyle: { color: "#F59E0B" },
};

export default function Reports() {
  const [monthly, setMonthly] = useState([]);
  const [yearly, setYearly] = useState([]);
  const [cats, setCats] = useState({ income: [], expense: [] });

  useEffect(() => {
    (async () => {
      const [m, y, c] = await Promise.all([
        api.get("/summary/monthly"),
        api.get("/summary/yearly"),
        api.get("/summary/categories"),
      ]);
      setMonthly(m.data);
      setYearly(y.data);
      setCats(c.data);
    })();
  }, []);

  async function exportPdf(kind) {
    try {
      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;
      const doc = new jsPDF();
      doc.setFontSize(18);
      doc.text(kind === "monthly" ? "Aylik Finansal Rapor" : "Yillik Finansal Rapor", 14, 20);
      doc.setFontSize(10);
      doc.setTextColor(120);
      doc.text(`Aves Elektrik Otomasyon · ${new Date().toLocaleDateString("tr-TR")}`, 14, 27);
      const data = kind === "monthly" ? monthly : yearly;
      const key = kind === "monthly" ? "month" : "year";
      const head = [[kind === "monthly" ? "Ay" : "Yil", "Gelir", "Gider", "Kar/Zarar"]];
      const body = data.map((r) => [
        r[key],
        r.income.toFixed(2) + " TL",
        r.expense.toFixed(2) + " TL",
        r.profit.toFixed(2) + " TL",
      ]);
      autoTable(doc, {
        startY: 35, head, body,
        headStyles: { fillColor: [11, 17, 28], textColor: [245, 158, 11] },
        styles: { fontSize: 10 },
      });
      const y0 = doc.lastAutoTable.finalY + 10;
      const totalIncome = data.reduce((a, b) => a + b.income, 0);
      const totalExpense = data.reduce((a, b) => a + b.expense, 0);
      doc.setTextColor(0);
      doc.text(`Toplam Gelir: ${totalIncome.toFixed(2)} TL`, 14, y0);
      doc.text(`Toplam Gider: ${totalExpense.toFixed(2)} TL`, 14, y0 + 6);
      doc.setFont("helvetica", "bold");
      doc.text(`Net Kar: ${(totalIncome - totalExpense).toFixed(2)} TL`, 14, y0 + 14);
      doc.save(`aves-${kind}-rapor-${new Date().toISOString().slice(0, 10)}.pdf`);
      toast.success("PDF indirildi");
    } catch (e) {
      toast.error("PDF oluşturulamadı");
    }
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="mono text-xs uppercase tracking-[0.3em] text-amber-400">Analitik</div>
          <h1 className="text-4xl font-bold tracking-tight mt-1 flex items-center gap-3">
            <PieIcon className="w-8 h-8 text-amber-400" /> Finansal Raporlar
          </h1>
          <p className="text-slate-400 mt-1 text-sm">Aylık ve yıllık kâr, gelir/gider dağılımı ve trend analizleri.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => exportPdf("monthly")} variant="outline" className="border-white/10" data-testid="export-monthly-pdf">
            <Download className="w-4 h-4 mr-1" /> Aylık PDF
          </Button>
          <Button onClick={() => exportPdf("yearly")} className="bg-amber-500 hover:bg-amber-400 text-black font-semibold" data-testid="export-yearly-pdf">
            <Download className="w-4 h-4 mr-1" /> Yıllık PDF
          </Button>
        </div>
      </header>

      <Tabs defaultValue="monthly">
        <TabsList className="bg-[#111723] border border-white/5">
          <TabsTrigger value="monthly" data-testid="tab-monthly">Aylık</TabsTrigger>
          <TabsTrigger value="yearly" data-testid="tab-yearly">Yıllık</TabsTrigger>
          <TabsTrigger value="category" data-testid="tab-category">Kategori</TabsTrigger>
        </TabsList>

        <TabsContent value="monthly" className="mt-6 space-y-5">
          <div className="card-elevated rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-1 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-400" /> Aylık Kâr / Zarar (Son 12 Ay)</h3>
            <p className="text-xs text-slate-400 mono uppercase tracking-widest mb-4">Gelir - Gider = Kâr</p>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip {...chartTooltip} formatter={(v) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Gelir" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Gider" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name="Kâr" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="card-elevated rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-4">Kâr Trend Çizgisi</h3>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip {...chartTooltip} formatter={(v) => formatMoney(v)} />
                <Line type="monotone" dataKey="profit" stroke="#F59E0B" strokeWidth={3} dot={{ fill: "#F59E0B", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>

        <TabsContent value="yearly" className="mt-6 space-y-5">
          <div className="card-elevated rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2"><DollarSign className="w-4 h-4 text-amber-400" /> Yıllık Performans</h3>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={yearly}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#94a3b8" }} />
                <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                <Tooltip {...chartTooltip} formatter={(v) => formatMoney(v)} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="income" name="Gelir" fill="#10B981" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expense" name="Gider" fill="#EF4444" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name="Kâr" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="overflow-x-auto card-elevated rounded-2xl">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.02]">
                <tr className="text-left text-[11px] uppercase mono tracking-widest text-slate-500 border-b border-white/5">
                  <th className="py-3 px-5">Yıl</th>
                  <th className="py-3 px-5">Gelir</th>
                  <th className="py-3 px-5">Gider</th>
                  <th className="py-3 px-5">Kâr / Zarar</th>
                </tr>
              </thead>
              <tbody>
                {yearly.map((y) => (
                  <tr key={y.year} className="border-b border-white/5">
                    <td className="py-3 px-5 mono">{y.year}</td>
                    <td className="py-3 px-5 tabular text-emerald-300">{formatMoney(y.income)}</td>
                    <td className="py-3 px-5 tabular text-red-300">{formatMoney(y.expense)}</td>
                    <td className={`py-3 px-5 tabular font-bold ${y.profit >= 0 ? "text-amber-300" : "text-red-300"}`}>{formatMoney(y.profit)}</td>
                  </tr>
                ))}
                {yearly.length === 0 && <tr><td colSpan={4} className="py-10 text-center text-slate-500">Veri yok</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="category" className="mt-6 grid grid-cols-1 xl:grid-cols-2 gap-5">
          <div className="card-elevated rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-4">Gelir Dağılımı</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={cats.income} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={(x) => x.category}>
                  {cats.income.map((entry) => <Cell key={`inc-${entry.category}`} fill={COLORS[cats.income.indexOf(entry) % COLORS.length]} />)}
                </Pie>
                <Tooltip {...chartTooltip} formatter={(v) => formatMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="card-elevated rounded-2xl p-6">
            <h3 className="text-lg font-semibold mb-4">Gider Dağılımı</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie data={cats.expense} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={100} label={(x) => x.category}>
                  {cats.expense.map((entry) => <Cell key={`exp-${entry.category}`} fill={COLORS[(cats.expense.indexOf(entry) + 2) % COLORS.length]} />)}
                </Pie>
                <Tooltip {...chartTooltip} formatter={(v) => formatMoney(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
